import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    FaGraduationCap,
    FaVideo,
    FaEdit,
    FaUserCheck,
    FaArrowRight,
    FaClock
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import WaveChart from '../../SuperAdmin/shared/WaveChart';
import Loader from '../../shared/Loader';
import './TutorDashboardOverview.css';

const TutorDashboardOverview = () => {
    const [stats, setStats] = useState({
        activeCourses: 0,
        totalStudents: 0,
        totalLiveClasses: 0,
        pendingTickets: 0
    });

    const [liveClassData, setLiveClassData] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            // Fetch Statistics
            const statsRes = await axios.get('/api/tutor/dashboard-stats', { headers });
            setStats(statsRes.data);

            // Fetch Upcoming Live Classes for the table
            const classesRes = await axios.get('/api/tutor/live-classes', { headers });
            // Sort by date and take the next few
            const upcoming = classesRes.data
                .filter(c => c.status === 'Scheduled' || c.status === 'Live')
                .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
                .slice(0, 5)
                .map(c => ({
                    id: c._id,
                    title: c.topic,
                    time: c.startTime,
                    duration: `${c.duration}m`,
                    students: c.attendance?.length || 0,
                    course: c.courseId?.title
                }));
            setLiveClassData(upcoming);

            setLoading(false);
        } catch (err) {
            console.error("Dashboard Sync Error:", err);
            setLoading(false);
        }
    };

    const classColumns = [
        { header: 'Class Title', key: 'title', width: '35%' },
        { header: 'Course', key: 'course', width: '20%' },
        { header: 'Time', key: 'time', width: '20%' },
        { header: 'Joined', key: 'students', width: '25%' }
    ];

    // Mock Attendance Chart Data (Kept as dummy per instructions)
    const attendanceData = [
        { name: 'Mon', attendance: 88 },
        { name: 'Tue', attendance: 94 },
        { name: 'Wed', attendance: 90 },
        { name: 'Thu', attendance: 96 },
        { name: 'Fri', attendance: 92 },
    ];

    // Mock Assignments Chart Data (Kept as dummy per instructions)
    const gradingData = [
        { name: 'Mon', graded: 14 },
        { name: 'Tue', graded: 25 },
        { name: 'Wed', graded: 18 },
        { name: 'Thu', graded: 30 },
        { name: 'Fri', graded: 22 },
    ];

    if (loading) return <Loader />;

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Morning, Tutor</h1>
                <p className="page-subtitle">Here is a summary of your classes, students, and tasks today.</p>
            </div>

            {/* KPI Cards */}
            <div className="kpi-grid">
                <div
                    className="kpi-card"
                    onClick={() => navigate('/admin/tutor-courses')}
                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <div className="kpi-header">
                        <span className="kpi-title">Active Courses</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(0, 44, 95, 0.1)', color: '#002C5F' }}><FaGraduationCap /></div>
                    </div>
                    <h2 className="kpi-value">{stats.activeCourses}</h2>
                    <div className="kpi-trend trend-neutral" style={{ color: '#002C5F', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>Manage Courses</span> <FaArrowRight fontSize="10px" />
                    </div>
                </div>

                <div
                    className="kpi-card"
                    onClick={() => navigate('/admin/tutor-attendance')}
                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <div className="kpi-header">
                        <span className="kpi-title">Total Students</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}><FaUserCheck /></div>
                    </div>
                    <h2 className="kpi-value">{stats.totalStudents}</h2>
                    <div className="kpi-trend trend-neutral" style={{ color: '#D4AF37', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>View Roster</span> <FaArrowRight fontSize="10px" />
                    </div>
                </div>

                <div
                    className="kpi-card"
                    onClick={() => navigate('/admin/tutor-live-classes')}
                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <div className="kpi-header">
                        <span className="kpi-title">Live Sessions</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(220, 38, 38, 0.1)', color: '#DC2626' }}><FaVideo /></div>
                    </div>
                    <h2 className="kpi-value">{stats.totalLiveClasses}</h2>
                    <div className="kpi-trend trend-neutral" style={{ color: '#DC2626', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>Go to Studio</span> <FaArrowRight fontSize="10px" />
                    </div>
                </div>

                <div
                    className="kpi-card"
                    onClick={() => navigate('/admin/tutor-support')}
                    style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <div className="kpi-header">
                        <span className="kpi-title">Pending Tickets</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10B981' }}><FaEdit /></div>
                    </div>
                    <h2 className="kpi-value">{stats.pendingTickets}</h2>
                    <div className="kpi-trend trend-neutral" style={{ color: '#10B981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>Open Support</span> <FaArrowRight fontSize="10px" />
                    </div>
                </div>
            </div>

            {/* Charts Section */}
            <div className="charts-grid">
                <div className="chart-card">
                    <div className="section-title">
                        <span>Attendance Trends (Last 5 Days)</span>
                    </div>
                    <WaveChart data={attendanceData} xKey="name" yKey="attendance" color="#10B981" />
                </div>
                <div className="chart-card">
                    <div className="section-title">
                        <span>Grading Activity</span>
                    </div>
                    <WaveChart data={gradingData} xKey="name" yKey="graded" color="#002C5F" />
                </div>
            </div>

            {/* Live Classes Table */}
            <DataTable
                title="Upcoming Live Classes"
                columns={classColumns}
                data={liveClassData}
                actions={
                    <button className="primary-btn sm pulse-on-hover" onClick={() => navigate('/admin/tutor-live-classes')}>
                        Go to Live Studio Workspace
                    </button>
                }
            />
        </div>
    );
};

export default TutorDashboardOverview;
